import React, { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Send, MapPin, Mail, MessageCircle, CheckCircle, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    subject: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language,
      });
      setIsSubmitted(true);
      toast.success(t('contact.form.success'));
      setFormData({
        name: '',
        email: '',
        phone: '',
        organization: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('contact.form.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 bg-oar-blue-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs md:text-sm uppercase tracking-[0.2em] text-oar-orange font-bold">
            Communication
          </span>
          <h1
            data-testid="contact-title"
            className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-white mt-4 mb-6"
          >
            {t('contact.title')}
          </h1>
          <p className="max-w-2xl text-lg text-gray-300">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-oar-bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-heading text-2xl text-oar-blue mb-8">
                {t('contact.info.title')}
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-oar-blue-soft rounded-sm flex-shrink-0">
                    <MapPin className="w-5 h-5 text-oar-blue" />
                  </div>
                  <div>
                    <div className="text-oar-text-primary font-medium mb-1">
                      {t('contact.info.address')}
                    </div>
                    <div className="text-oar-text-secondary text-sm">
                      {t('contact.info.addressValue')}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-oar-blue-soft rounded-sm flex-shrink-0">
                    <Mail className="w-5 h-5 text-oar-blue" />
                  </div>
                  <div>
                    <div className="text-oar-text-primary font-medium mb-1">
                      {t('contact.info.email')}
                    </div>
                    <a
                      href="mailto:contact@russophonie-afrique.com"
                      className="text-oar-text-secondary text-sm hover:text-oar-blue transition-colors"
                    >
                      contact@russophonie-afrique.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-oar-green-soft rounded-sm flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-oar-green" />
                  </div>
                  <div>
                    <div className="text-oar-text-primary font-medium mb-1">
                      WhatsApp
                    </div>
                    <a
                      href="https://wa.me/23670155252"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-oar-text-secondary text-sm hover:text-oar-green transition-colors"
                    >
                      +236 70 15 52 52
                    </a>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-12 pt-8 border-t border-oar-border">
                <div className="text-oar-text-muted text-xs uppercase tracking-wider mb-4">
                  Suivez-nous
                </div>
                <div className="flex gap-4">
                  <a
                    href="#"
                    className="w-10 h-10 flex items-center justify-center border border-oar-border rounded-sm hover:border-oar-blue hover:text-oar-blue text-oar-text-secondary transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                  <a
                    href="https://wa.me/23670155252"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center border border-oar-border rounded-sm hover:border-oar-green hover:text-oar-green text-oar-text-secondary transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 flex items-center justify-center border border-oar-border rounded-sm hover:border-oar-blue hover:text-oar-blue text-oar-text-secondary transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {isSubmitted ? (
                <div className="bg-white border border-oar-green/30 rounded-sm p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-oar-green mx-auto mb-6" />
                  <h3 className="font-heading text-2xl text-oar-text-primary mb-4">
                    {t('contact.form.success')}
                  </h3>
                  <p className="text-oar-text-secondary">
                    Nous vous répondrons dans les plus brefs délais.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="mt-8 text-oar-blue hover:text-oar-blue-light transition-colors"
                  >
                    Envoyer un autre message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white border border-oar-border rounded-sm p-8 shadow-sm">
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="name" className="block text-oar-text-secondary text-sm mb-2">
                        {t('contact.form.name')} *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="contact-name-input"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary placeholder:text-oar-text-muted rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-oar-blue transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-oar-text-secondary text-sm mb-2">
                        {t('contact.form.email')} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-email-input"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary placeholder:text-oar-text-muted rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-oar-blue transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="phone" className="block text-oar-text-secondary text-sm mb-2">
                        <span className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          Téléphone
                        </span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+XXX XX XX XX XX"
                        data-testid="contact-phone-input"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary placeholder:text-oar-text-muted rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-oar-blue transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="organization" className="block text-oar-text-secondary text-sm mb-2">
                        {t('contact.form.organization')}
                      </label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        data-testid="contact-organization-input"
                        className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary placeholder:text-oar-text-muted rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-oar-blue transition-colors"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="subject" className="block text-oar-text-secondary text-sm mb-2">
                      {t('contact.form.subject')} *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      data-testid="contact-subject-input"
                      className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary placeholder:text-oar-text-muted rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-oar-blue transition-colors"
                    />
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="block text-oar-text-secondary text-sm mb-2">
                      {t('contact.form.message')} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      data-testid="contact-message-input"
                      className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary placeholder:text-oar-text-muted rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-oar-blue transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    data-testid="contact-submit-btn"
                    className="flex items-center justify-center gap-2 bg-oar-red hover:bg-oar-red-dark disabled:opacity-50 text-white rounded-sm px-8 py-4 font-medium transition-all duration-300 w-full md:w-auto"
                  >
                    {isLoading ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        {t('contact.form.submit')}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
